/**
 * DocDetailDrawer — slide-in panel surfacing the per-doc metadata
 * that's been hidden from non-admin users until now.
 *
 * Sections (top-to-bottom):
 *   - File header (filename, size, mime, upload date)
 *   - Summary + Re-summarize (calls summarizeDocument)
 *   - Tags chip editor
 *   - Relevance heatmap (per-report-type 0-1 bars)
 *   - Used in (report types referencing this doc)
 *
 * The audit-log section the spec mentions is omitted because
 * auditLogClient.ts ships emit-only — no queryByDocument helper
 * exists today. Add the section when that helper lands.
 *
 * Persistence: every mutation funnels through `onChange(updated)`
 * so the host (DocumentLibrary) stays the single owner of the
 * commit pipeline. No store reads here — the drawer is a pure
 * controlled component over the doc passed in.
 */
import React, { useState, useMemo } from 'react';
import { X, Sparkles, Loader2, Plus, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import type { SiteDocument, SiteRagReport } from '../../types/siteRag';
import type { SiteEntity } from '../../types/site';
import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import { usedInReportTypes } from '../../lib/documentUsage';
import { summarizeDocument } from '../../services/knowledge/driveConnector';
import { DocExtractedFieldsPanel } from './DocExtractedFieldsPanel';
import { documentBodyText, hydrateDocumentBodiesForEvidence, type DocumentBodyLoader } from '../../lib/documentBody';

interface Props {
  bodyLoader?: DocumentBodyLoader;
  /** When non-null, the drawer slides in. Null = closed. */
  doc: SiteDocument | null;
  onClose: () => void;
  /** Persist a single-doc update. Host fans this into its full
   *  documents array and routes to the commit pipeline. */
  onChange: (updated: SiteDocument) => Promise<void> | void;
  /** Reports + site for the "Used in" computation. Optional —
   *  when missing the section just hides itself. */
  reports?: SiteRagReport[];
  site?: SiteEntity;
  /** Click handler for the Preview button — host opens its
   *  DocViewerDialog (lazy-loaded). */
  onPreview?: (doc: SiteDocument) => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Bucket a 0-1 relevance score into a colour band:
 *   ≥ 0.8 → green (strong fit),
 *   0.5..0.8 → amber (some fit),
 *   < 0.5 → grey (weak / unknown).
 *  Same thresholds as the upstream classifier's confidence buckets
 *  so the heatmap colours match the rest of the UI. */
function relevanceColour(score: number): string {
  if (score >= 0.8) return 'bg-status-success';
  if (score >= 0.5) return 'bg-status-warning';
  return 'bg-text-muted/40';
}

export const DocDetailDrawer: React.FC<Props> = ({ doc, onClose, onChange, reports, site, onPreview, bodyLoader }) => {
  const [tagInput, setTagInput] = useState('');
  const [resummarizing, setResummarizing] = useState(false);

  // Used-in is a pure projection of reports + site for this doc.
  const usedTypes = useMemo<ReportTypeId[]>(() => {
    if (!doc || !reports) return [];
    return usedInReportTypes(doc.id, reports, site);
  }, [doc, reports, site]);

  // Sort relevance entries descending so the strongest fits surface
  // at the top of the heatmap. Filter out zero-or-undefined scores —
  // the spec's test pins this so an unscored type doesn't claim a row.
  const relevanceEntries = useMemo<Array<[ReportTypeId, number]>>(() => {
    if (!doc?.relevance) return [];
    const entries: Array<[ReportTypeId, number]> = [];
    for (const id of Object.keys(REPORT_TYPE_REGISTRY) as ReportTypeId[]) {
      const score = doc.relevance[id];
      if (typeof score === 'number' && score > 0) entries.push([id, score]);
    }
    entries.sort((a, b) => b[1] - a[1]);
    return entries;
  }, [doc]);

  if (!doc) return null;

  const handleAddTag = async () => {
    const t = tagInput.trim();
    if (!t) return;
    const existing = doc.tags ?? [];
    if (existing.includes(t)) {
      setTagInput('');
      return;
    }
    try {
      await onChange({ ...doc, tags: [...existing, t] });
      setTagInput('');
    } catch (error) {
      toast.error(`Document tags were not saved: ${(error as Error).message}`);
    }
  };

  const handleRemoveTag = async (tag: string) => {
    try {
      await onChange({ ...doc, tags: (doc.tags ?? []).filter(t => t !== tag) });
    } catch (error) {
      toast.error(`Document tags were not saved: ${(error as Error).message}`);
    }
  };

  const handleResummarize = async () => {
    setResummarizing(true);
    try {
      const [source] = await hydrateDocumentBodiesForEvidence([doc], { loader: bodyLoader });
      const { summary, tags } = await summarizeDocument(documentBodyText(source!), doc.fileName);
      // Merge the model's tags into the existing set (don't blow
      // away the user's manual additions).
      const mergedTags = Array.from(new Set([...(doc.tags ?? []), ...tags]));
      await onChange({ ...doc, summary, tags: mergedTags });
      toast.success('Summary refreshed');
    } catch (e) {
      toast.error(`Re-summarize failed: ${(e as Error).message ?? 'unknown'}`);
    } finally {
      setResummarizing(false);
    }
  };

  // Slide-in via tw-animate-css utilities (same lib Popover uses).
  // Plain CSS keeps the drawer dependency-light — `motion` was
  // pulling @emotion/is-prop-valid through a runtime require() that
  // the bundle guard correctly rejected.
  return (
    <aside
      key="doc-detail-drawer"
      data-testid="doc-detail-drawer"
      className="fixed top-0 inset-x-0 sm:inset-x-auto sm:right-0 h-full w-full sm:w-[420px] md:w-[480px] lg:w-[560px] xl:w-[640px] bg-background border-l border-border shadow-2xl z-40 flex flex-col animate-in slide-in-from-right-8 fade-in-0 duration-200"
    >
        <div className="px-4 py-3 border-b border-border flex items-start gap-3 shrink-0">
          <div className="flex-1 min-w-0">
            <div className="type-body uppercase tracking-widest text-text-muted">Document</div>
            <h3
              className="text-sm font-bold text-foreground break-words"
              title={doc.fileName}
              data-testid="doc-detail-filename"
            >
              {doc.fileName}
            </h3>
            <div className="text-[10px] text-text-secondary mt-1 space-y-0.5">
              <div>{formatSize(doc.sizeBytes)} · {doc.mimeType || 'unknown type'}</div>
              <div>Uploaded {new Date(doc.uploadedAt).toLocaleString()}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-text-secondary hover:text-text-primary"
            title="Close"
            data-testid="doc-detail-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-5">
          {onPreview && (
            <Button
              size="sm"
              variant="outline"
              className="w-full h-8 type-body uppercase tracking-widest"
              onClick={() => onPreview(doc)}
              data-testid="doc-detail-preview"
            >
              <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview
            </Button>
          )}

          {/* KSD3 — Include / exclude this doc from SED extraction.
               Defaults to "in SED"; user-set false flips it. The
               bootstrap pipeline reads this flag and skips excluded
               docs entirely. Composes with the UD pack (preserved
               across rebuilds keyed on docId). */}
          <section className="space-y-1.5" data-testid="doc-detail-include-in-sed">
            <h4 className="type-body uppercase tracking-widest text-text-muted font-bold">
              SED participation
            </h4>
            <Button
              size="sm"
              variant="outline"
              className={`w-full h-8 type-body uppercase tracking-widest ${
                doc.includeInSed === false ? 'border-danger text-danger' : ''
              }`}
              onClick={async () => {
                const before = doc.includeInSed !== false;
                const nextValue = !before;
                await onChange({ ...doc, includeInSed: nextValue });
                toast.success(nextValue
                  ? `${doc.fileName} included in SED extraction`
                  : `${doc.fileName} excluded from SED extraction`,
                );
              }}
              data-testid={`doc-detail-include-in-sed-${doc.id}`}
            >
              {doc.includeInSed === false
                ? '⊘ Excluded from SED — click to include'
                : '✓ In SED — click to exclude'}
            </Button>
            <p className="text-[10px] text-text-muted leading-snug">
              {doc.includeInSed === false
                ? 'This document is skipped during SED rebuilds — no extraction, no boost.'
                : 'This document contributes to SED extraction. Excluding is useful for duplicate revisions or irrelevant docs.'}
            </p>
          </section>

          {/* Extracted fields — what this doc put into the evidence, where
              it is used, and inline Accept/Edit/Reject. Only renders in
              site context (the SED lives on the site). */}
          {site && (
            <section className="space-y-1.5" data-testid="doc-detail-extracted-fields">
              <h4 className="type-body uppercase tracking-widest text-text-muted font-bold">
                Extracted fields
              </h4>
              <DocExtractedFieldsPanel doc={doc} site={site} />
            </section>
          )}

          {/* Summary */}
          <section className="space-y-1.5" data-testid="doc-detail-summary">
            <div className="flex items-center justify-between">
              <h4 className="type-body uppercase tracking-widest text-text-muted font-bold">Summary</h4>
              <button
                onClick={() => void handleResummarize()}
                disabled={resummarizing}
                className="type-body uppercase tracking-widest text-primary hover:text-primary/80 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="doc-detail-resummarize"
              >
                {resummarizing
                  ? <><Loader2 className="w-3 h-3 animate-spin" /> Working…</>
                  : <><Sparkles className="w-3 h-3" /> Re-summarize</>}
              </button>
            </div>
            <p
              className="text-xs text-text-primary leading-relaxed whitespace-pre-line"
              data-testid="doc-detail-summary-text"
            >
              {doc.summary?.trim() || <span className="italic text-text-muted">No summary yet.</span>}
            </p>
          </section>

          {/* Tags */}
          <section className="space-y-1.5" data-testid="doc-detail-tags">
            <h4 className="type-body uppercase tracking-widest text-text-muted font-bold">Tags</h4>
            <div className="flex flex-wrap gap-1">
              {(doc.tags ?? []).map(tag => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border border-border bg-state-hover"
                  data-testid={`doc-detail-tag-${tag}`}
                >
                  <span>{tag}</span>
                  <button
                    onClick={() => void handleRemoveTag(tag)}
                    className="text-text-muted hover:text-status-danger"
                    title={`Remove "${tag}"`}
                    data-testid={`doc-detail-tag-remove-${tag}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
              {(doc.tags ?? []).length === 0 && (
                <span className="text-[10px] text-text-muted italic">No tags.</span>
              )}
            </div>
            <div className="flex items-center gap-1.5 pt-1">
              <input
                type="text"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') void handleAddTag(); }}
                placeholder="Add tag…"
                className="flex-1 h-7 px-2 text-[10px] border border-border bg-background"
                data-testid="doc-detail-tag-input"
              />
              <button
                onClick={() => void handleAddTag()}
                disabled={!tagInput.trim()}
                className="h-7 px-2 type-body uppercase tracking-widest border border-border bg-state-hover hover:bg-muted disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="doc-detail-tag-add"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
          </section>

          {/* Relevance heatmap */}
          <section className="space-y-1.5" data-testid="doc-detail-relevance">
            <h4 className="type-body uppercase tracking-widest text-text-muted font-bold">
              Relevance by report type
            </h4>
            {relevanceEntries.length === 0 ? (
              <div className="text-[10px] text-text-muted italic">
                Not yet classified. Re-summarize or upload again to score this doc.
              </div>
            ) : (
              <ul className="space-y-1">
                {relevanceEntries.map(([id, score]) => {
                  const meta = REPORT_TYPE_REGISTRY[id];
                  return (
                    <li
                      key={id}
                      className="grid grid-cols-[110px_1fr_36px] items-center gap-2"
                      data-testid={`doc-detail-relevance-${id}`}
                    >
                      <span className="text-[10px] text-text-secondary truncate" title={meta?.label ?? id}>
                        {meta?.shortLabel ?? id}
                      </span>
                      <div className="h-2 bg-state-hover relative overflow-hidden">
                        <div
                          className={`h-full ${relevanceColour(score)} transition-all`}
                          style={{ width: `${Math.min(100, Math.max(0, score * 100))}%` }}
                        />
                      </div>
                      <span className="text-[10px] font-mono text-text-secondary text-right">
                        {score.toFixed(2)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Used in */}
          {usedTypes.length > 0 && (
            <section className="space-y-1.5" data-testid="doc-detail-used-in">
              <h4 className="type-body uppercase tracking-widest text-text-muted font-bold">
                Used in
              </h4>
              <div className="flex flex-wrap gap-1">
                {usedTypes.map(t => {
                  const meta = REPORT_TYPE_REGISTRY[t];
                  return (
                    <span
                      key={t}
                      className="type-meta px-1.5 py-0.5 rounded-md border border-primary/30 text-primary bg-primary/5"
                      title={meta?.label ?? t}
                    >
                      {meta?.shortLabel ?? t}
                    </span>
                  );
                })}
              </div>
            </section>
          )}
        </div>
    </aside>
  );
};
